import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Message } from 'primeng/message';
import { Tooltip } from 'primeng/tooltip';
import { CadObject } from '../../../../../../../models/cad-object.model';
import { QuoteItemData } from '../../../../../../../models/quote-item-data.model';
import { CadViewerComponent } from '../../../../../../cad-viewer/cad-viewer.component';

export interface LinkTreeNode {
  itemId: string;
  name: string;
  children: LinkTreeNode[];
}

@Component({
  selector: 'app-quote-item-link-file',
  imports: [
    ButtonModule,
    CadViewerComponent,
    Checkbox,
    FormsModule,
    Message,
    NgTemplateOutlet,
    Tooltip,
  ],
  templateUrl: './quote-item-link-file.component.html',
  styleUrl: './quote-item-link-file.component.scss',
})
export class QuoteItemLinkFileComponent implements OnChanges {
  @Input() linkItems: QuoteItemData[] = [];

  @Input() set loadedCadObjects(value: CadObject[]) {
    this.cadObjects.set(value);
    const visibleCadObjectIds = new Set<number>();
    this.addCadObjectIdsToSet(value, visibleCadObjectIds);
    this.visibleCadObjectIds.set(visibleCadObjectIds);
  }

  @Output() confirmed = new EventEmitter<QuoteItemData[]>();
  @Output() back = new EventEmitter<void>();

  private cadObjects = signal<CadObject[]>([]);
  // Shared between the CAD tree and the viewer so hovering either highlights both.
  hoveredCadObjectId = signal<number | null>(null);
  visibleCadObjectIds = signal<Set<number>>(new Set());

  private addCadObjectIdsToSet(
    cadObjects: CadObject[],
    ids: Set<number>,
  ): void {
    for (const cadObject of cadObjects) {
      if (!cadObject.children?.length) {
        ids.add(cadObject.id);
      } else {
        this.addCadObjectIdsToSet(cadObject.children, ids);
      }
    }
  }

  // Working copy of items
  private items = signal<QuoteItemData[]>([]);
  // Item actively being linked
  activelyLinkingItemId = signal<string | null>(null);
  // User-set mappings: item persistent_id -> cad object ids
  private itemCadMappings = signal<Map<string, number[]>>(new Map());
  // Expansion state for the item tree
  expandedItemIds = signal<Set<string>>(new Set());
  // Expansion state for CAD object tree
  expandedIds = signal<Set<number>>(new Set());
  // Checkbox selection buffer
  checkedCadObjectIds = signal<Set<number>>(new Set());
  // Whether to show the unlinked-items warning
  showUnlinkedWarning = signal(false);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------
  private rootItems = computed(() => {
    const items = this.items();
    if (items.length <= 1) return items;
    return items.filter((item) => item.parent_id === null);
  });

  isLinking = computed(() => this.activelyLinkingItemId() !== null);

  hasUnlinkedItems = computed(() =>
    this.items().every(
      (item) =>
        (this.itemCadMappings().get(item.persistent_id) ?? []).length === 0,
    ),
  );

  availableCadObjects = computed(() =>
    this.cadObjects().filter((cadObject) => cadObject.parent_id == null),
  );

  allCadObjectsList = computed(() =>
    Array.from(this.allCadObjectsMap().values()),
  );

  viewerCadObjects = computed(() =>
    this.allCadObjectsList().filter((obj) => !obj.children?.length),
  );

  linkTree = computed((): LinkTreeNode[] => {
    const items = this.items();
    if (!items.length) return [];

    const childrenByParent = new Map<string, QuoteItemData[]>();
    for (const item of items) {
      if (item.parent_id !== null) {
        const siblings = childrenByParent.get(item.parent_id) ?? [];
        siblings.push(item);
        childrenByParent.set(item.parent_id, siblings);
      }
    }

    const toNode = (item: QuoteItemData): LinkTreeNode => {
      const children = (childrenByParent.get(item.persistent_id) ?? [])
        .slice()
        .sort((a, b) => a.sibling_order - b.sibling_order)
        .map(toNode);

      return {
        itemId: item.persistent_id,
        name: item.name || 'New Item',
        children,
      };
    };

    return this.rootItems()
      .slice()
      .sort((a, b) => a.sibling_order - b.sibling_order)
      .map(toNode);
  });

  private allCadObjectsMap = computed(() => {
    const map = new Map<number, CadObject>();
    const walk = (objects: CadObject[]) => {
      for (const obj of objects) {
        map.set(obj.id, obj);
        if (obj.children?.length) walk(obj.children);
      }
    };
    walk(this.availableCadObjects());
    return map;
  });

  // The row showing the hovered object: the object itself, or its outermost
  // collapsed ancestor when it is hidden in the tree.
  private hoveredRowId = computed(() => {
    const map = this.allCadObjectsMap();
    const expandedIds = this.expandedIds();
    let rowId = this.hoveredCadObjectId();
    let current = rowId === null ? undefined : map.get(rowId);
    while (current?.parent_id != null) {
      if (!expandedIds.has(current.parent_id)) rowId = current.parent_id;
      current = map.get(current.parent_id);
    }
    return rowId;
  });

  checkedCadObjects = computed(() => {
    const ids = this.checkedCadObjectIds();
    const map = this.allCadObjectsMap();
    return Array.from(ids)
      .map((id) => map.get(id))
      .filter((o): o is CadObject => !!o);
  });

  // CAD object id -> ids of every item it is linked to.
  private cadObjectLinkedItemIds = computed(() => {
    const map = new Map<number, string[]>();
    for (const [itemId, cadIds] of this.itemCadMappings().entries()) {
      for (const cadId of cadIds) {
        map.set(cadId, [...(map.get(cadId) ?? []), itemId]);
      }
    }
    return map;
  });

  private itemNodeNameMap = computed(() => {
    const map = new Map<string, string>();
    const walk = (nodes: LinkTreeNode[]) => {
      for (const node of nodes) {
        map.set(node.itemId, node.name);
        if (node.children.length) walk(node.children);
      }
    };
    walk(this.linkTree());
    return map;
  });

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['linkItems']) {
      this.initializeLinkItems(this.linkItems);
    }
  }

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  onSave(): void {
    if (this.hasUnlinkedItems() && !this.showUnlinkedWarning()) {
      this.showUnlinkedWarning.set(true);
      return;
    }
    const allCadObjects = this.allCadObjectsMap();
    const linkedItems = this.items().map((item) => {
      const cadIds = this.itemCadMappings().get(item.persistent_id) ?? [];
      const cadNames = cadIds
        .map((id) => allCadObjects.get(id)?.name)
        .filter((n): n is string => !!n);
      return {
        ...item,
        name: cadNames[0] ?? item.name,
        cad_object_ids: cadIds,
        cad_object_names: cadNames,
      };
    });
    this.confirmed.emit(linkedItems);
  }

  onBack(): void {
    this.activelyLinkingItemId.set(null);
    this.checkedCadObjectIds.set(new Set());
    this.back.emit();
  }

  // ---------------------------------------------------------------------------
  // Item tree interactions
  // ---------------------------------------------------------------------------
  isItemExpanded(itemId: string): boolean {
    return this.expandedItemIds().has(itemId);
  }

  toggleItemExpanded(itemId: string): void {
    this.expandedItemIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }

  getItemCadObjectIds(itemId: string): number[] {
    return this.itemCadMappings().get(itemId) || [];
  }

  getItemLinkedCadObjects(itemId: string): CadObject[] {
    const ids = this.getItemCadObjectIds(itemId);
    const map = this.allCadObjectsMap();
    return ids.map((id) => map.get(id)).filter((o): o is CadObject => !!o);
  }

  startLinking(mappingKey: string): void {
    this.activelyLinkingItemId.set(mappingKey);
    const existingIds = this.getItemCadObjectIds(mappingKey);
    this.checkedCadObjectIds.set(new Set(existingIds));
  }

  cancelLinking(): void {
    this.activelyLinkingItemId.set(null);
    this.checkedCadObjectIds.set(new Set());
  }

  saveLinking(): void {
    const itemId = this.activelyLinkingItemId();
    if (!itemId) return;
    const newIds = Array.from(this.checkedCadObjectIds());
    this.itemCadMappings.update((map) => {
      const next = new Map(map);
      next.set(itemId, newIds);
      return next;
    });
    this.activelyLinkingItemId.set(null);
    this.checkedCadObjectIds.set(new Set());
    // Dismiss the warning if all items are now linked
    if (!this.hasUnlinkedItems()) {
      this.showUnlinkedWarning.set(false);
    }
  }

  // ---------------------------------------------------------------------------
  // CAD tree interactions
  // ---------------------------------------------------------------------------
  getLinkedTooltip(cadObject: CadObject): string {
    const names = (this.cadObjectLinkedItemIds().get(cadObject.id) ?? []).map(
      (itemId) => this.itemNodeNameMap().get(itemId) ?? 'Unknown',
    );
    return names.length
      ? `Linked to ${names.map((n) => `'${n}'`).join(', ')}`
      : 'Linked';
  }

  isLinked(cadObject: CadObject): boolean {
    return this.cadObjectLinkedItemIds().has(cadObject.id);
  }

  isHovered(cadObject: CadObject): boolean {
    return this.hoveredRowId() === cadObject.id;
  }

  setHover(cadObject: CadObject, hover: boolean): void {
    if (hover) {
      this.hoveredCadObjectId.set(cadObject.id);
    } else if (this.hoveredCadObjectId() === cadObject.id) {
      this.hoveredCadObjectId.set(null);
    }
  }

  setVisibility(cadObject: CadObject, visible: boolean): void {
    this.visibleCadObjectIds.update((ids) => {
      const next = new Set(ids);
      if (visible) {
        next.add(cadObject.id);
      } else {
        next.delete(cadObject.id);
      }
      return next;
    });
  }

  isCadObjectVisible(cadObject: CadObject): boolean {
    return this.visibleCadObjectIds().has(cadObject.id);
  }

  isExpanded(cadObject: CadObject): boolean {
    return this.expandedIds().has(cadObject.id);
  }

  toggleExpanded(cadObject: CadObject): void {
    this.expandedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(cadObject.id)) {
        next.delete(cadObject.id);
      } else {
        next.add(cadObject.id);
      }
      return next;
    });
  }

  isChecked(cadObject: CadObject): boolean {
    return this.checkedCadObjectIds().has(cadObject.id);
  }

  toggleChecked(cadObject: CadObject): void {
    this.checkedCadObjectIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(cadObject.id)) {
        next.delete(cadObject.id);
      } else {
        next.add(cadObject.id);
      }
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------
  private initializeLinkItems(items: QuoteItemData[]): void {
    this.items.set(items.map((item) => ({ ...item })));
    this.showUnlinkedWarning.set(false);

    // Filter out stale CAD object IDs that don't exist in the currently loaded
    // CAD objects (e.g. from a previous upload session).
    const validCadIds = this.allCadObjectsMap();
    const mappings = new Map<string, number[]>();
    for (const item of items) {
      const cadIds = (item.cad_object_ids ?? []).filter((id) =>
        validCadIds.has(id),
      );
      mappings.set(item.persistent_id, cadIds);
    }

    const rootItems = this.rootItems();
    const rootCadObjects = this.availableCadObjects();
    if (rootItems.length === 1 && rootCadObjects.length === 1) {
      const rootItem = rootItems[0];
      const existing = mappings.get(rootItem.persistent_id) ?? [];
      if (existing.length === 0) {
        mappings.set(rootItem.persistent_id, [rootCadObjects[0].id]);
      }
    }

    this.itemCadMappings.set(mappings);

    this.expandedItemIds.set(
      new Set(this.rootItems().map((item) => item.persistent_id)),
    );
  }
}
